import { useState, useMemo } from 'react';
import { Image, Form, Row, Col, Button, Card} from 'react-bootstrap';
import ProfileMobileSectionActions from './ProfileMobileSectionActions';
import countries from './data/countries'
import icValidationError from './assets/img/ic-danger.svg';
import icNationality from './assets/img/ic-flag.svg';
import icCountry from './assets/img/ic-globe.svg';
import icCity from './assets/img/ic-city.svg';

const UserAddResidence = ({
    handleResidenceSubmit,
    handleReset,
    formData,
    handleChange,
    errors,
    isEditPage,
    hideEdit,
    clearCityOnCountryChange = false,
    resolveCountryValue = false,
    zipOptional = false,
}) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [localErrors, setLocalErrors] = useState({});

    const uniqueCountries = Array.from(
        new Map(countries.map(c => [c.id, c])).values()
    );

    const countryOptions = uniqueCountries.map(c => ({
        value: c.id,
        label: c.name
    }));

    const countryIdToName = useMemo(
        () => Object.fromEntries(uniqueCountries.map(c => [String(c.id), c.name])),
        [uniqueCountries]
    );

    const countryNameToId = useMemo(
        () =>
            Object.fromEntries(
                uniqueCountries.map(c => [String(c.name || '').trim().toLowerCase(), String(c.id)])
            ),
        [uniqueCountries]
    );

    const resolveCountryId = (rawValue) => {
        const raw = String(rawValue || '').trim();
        if (!raw) return '';
        if (countryIdToName[raw]) return raw;
        return countryNameToId[raw.toLowerCase()] || '';
    };

    const getCountryDisplayName = (rawValue) => {
        const raw = String(rawValue || '').trim();
        if (!raw) return 'N/A';
        return countryIdToName[raw] || raw;
    };

    const resolvedCitizenship1Id = resolveCountryId(formData?.citizenships?.[0] || formData?.nationality || '');
    const resolvedCitizenship2Id = resolveCountryId(formData?.citizenships?.[1] || '');
    const resolvedCountryId = resolveCountryId(formData?.country || '');

    const citizenship2Options = countryOptions.filter(
        (opt) => String(opt.value) !== String(resolvedCitizenship1Id || '')
    );

    const handleCitizenship1Change = (e) => {
        const { value } = e.target;

        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, citizenship1: 'Citizenship #1 is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, citizenship1: '' }));
        }

        const citizenship2Value = String(formData?.citizenships?.[1] || '').trim();
        const nextCitizenships = [value];

        if (citizenship2Value && citizenship2Value !== value) {
            nextCitizenships.push(citizenship2Value);
        }

        handleChange({
            target: {
                name: 'citizenships',
                value: nextCitizenships.slice(0, 2),
            },
        });

        handleChange({ target: { name: 'nationality', value } });
    };

    const handleCitizenship2Change = (e) => {
        const { value } = e.target;

        const citizenship1Value = resolveCountryId(formData?.citizenships?.[0] || formData?.nationality || '');

        handleChange({
            target: {
                name: 'citizenships',
                value: [citizenship1Value, value].filter(Boolean).slice(0, 2),
            },
        });
    };

    const handleCountryChange = (e) => {
        const { value } = e.target;
        const countryChanged = String(value) !== String(resolvedCountryId);

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, country: 'Country is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, country: '' }));
        }

        handleChange({ target: { name: 'country', value } });

        if (clearCityOnCountryChange && countryChanged && formData?.city) {
            handleChange({ target: { name: 'city', value: '' } });
            setLocalErrors((prev) => ({ ...prev, city: '' }));
        }
    };

    const handleCityChange = (e) => {
        const { value } = e.target;

        // Live validation
        if (!value.trim()) {
            setLocalErrors((prev) => ({ ...prev, city: 'City is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, city: '' }));
        }

        handleChange({ target: { name: 'city', value } });
    };

    const handleZipChange = (e) => {
        const { value } = e.target;

        if (!zipOptional && !value.trim()) {
            setLocalErrors((prev) => ({ ...prev, zipCity: 'Zip is required.' }));
        } else {
            setLocalErrors((prev) => ({ ...prev, zipCity: '' }));
        }

        handleChange({ target: { name: 'zipCity', value } });
    };

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        if (typeof handleResidenceSubmit === 'function') {
            const success = await handleResidenceSubmit(e); // ✅ wait for result
            if (success) {
                setIsEditing(false); // ✅ Only if no validation/API errors
            }
        } else {
            console.error('Error: handleResidenceSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false); // Exit edit mode

        if (typeof handleReset === 'function') {
            handleReset(tempFormData); // ✅ Restore saved values + clear errors
        }
    };

    return (
        <>
        <Card className="mb-4 profile-section-card">
            <Card.Body>
                    {/* Title and Edit Button */}
                    <Row>
                        <Col>
                            <div className="profile-section-heading-row">
                            {isEditPage && !hideEdit && (
                                <div className="float-end d-none d-lg-block">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3 className="company-detail-section-title d-lg-none"><span className="item-number">2.</span> Nationality & residence info</h3>
                            <h3 className="d-none d-lg-flex">2. Nationality & residence info</h3>
                            </div>
                            <hr className="d-lg-none" />
                        </Col>
                    </Row>
                    
                    {isEditing ? (
                        <Row>
                            {/* Form Fields */}
                            <Col md={12} className="profile-edit-fields">
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="validated mb-3" controlId="citizenship1">
                                            <Form.Label>Citizenship <span>*</span></Form.Label>
                                            <Form.Select
                                                name="citizenship1"
                                                value={resolvedCitizenship1Id}
                                                onChange={handleCitizenship1Change}
                                                isInvalid={!!errors.citizenships || !!localErrors.citizenship1}
                                                required
                                                disabled={!isEditing}
                                            >
                                                <option value="">Select citizenship</option>
                                                {countryOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {(errors.citizenships || localErrors.citizenship1) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.citizenships || localErrors.citizenship1}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.citizenships || localErrors.citizenship1}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                        <Form.Group className="validated" controlId="citizenship2">
                                            <Form.Label>Citizenship #2</Form.Label>
                                            <Form.Select
                                                name="citizenship2"
                                                value={resolvedCitizenship2Id}
                                                onChange={handleCitizenship2Change}
                                                disabled={!isEditing}
                                            >
                                                <option value="">None</option>
                                                {citizenship2Options.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="country">
                                            <Form.Label>Country <span>*</span></Form.Label>
                                            <Form.Select
                                                name="country"
                                                onChange={handleCountryChange}
                                                isInvalid={!!errors.country || !!localErrors.country}
                                                value={resolveCountryValue ? resolvedCountryId : formData.country}
                                                disabled={!isEditing}
                                            >
                                                <option value="">Select country</option>
                                                {countryOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {(errors.country || localErrors.country) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.country || localErrors.country}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.country || localErrors.country}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="city">
                                            <Form.Label>City <span>*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleCityChange}
                                                isInvalid={!!errors.city || !!localErrors.city}
                                                required
                                                disabled={!isEditing}
                                            />
                                            {(errors.city || localErrors.city) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.city || localErrors.city}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.city || localErrors.city}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="zipCity">
                                            <Form.Label>Zip code {!zipOptional && <span>*</span>}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="zipCity"
                                                value={formData.zipCity}
                                                onChange={handleZipChange}
                                                isInvalid={!!errors.zipCity || !!localErrors.zipCity}
                                                required={!zipOptional}
                                                disabled={!isEditing}
                                            />
                                            {(errors.zipCity || localErrors.zipCity) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.zipCity || localErrors.zipCity}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.zipCity || localErrors.zipCity}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    ) : (
                        <Row>  
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icNationality} alt="Nationality" />
                                            </span>
                                            <span className="text">
                                                Citizenship
                                                <br />
                                                <strong>
                                                    {countryIdToName[String(formData?.citizenships?.[0] || formData?.nationality)] || "N/A"}
                                                    {formData?.citizenships?.[1]
                                                        ? `, ${countryIdToName[String(formData.citizenships[1])] || formData.citizenships[1]}`
                                                        : ''}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icCountry} alt="Country" />
                                            </span>
                                            <span className="text">
                                                Country
                                                <br />
                                                <strong>
                                                    {countryIdToName[String(formData.country)] || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icCity} alt="City" />
                                            </span>
                                            <span className="text">
                                                City
                                                <br />
                                                <strong>
                                                    {formData.city || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="icon-text">
                                            <span className="icon" aria-hidden="true">
                                                <Image src={icCity} alt="Zip  Code" />
                                            </span>
                                            <span className="text">
                                                Zip Code
                                                <br />
                                                <strong>
                                                    {formData.zipCity || "N/A"}
                                                </strong>
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    )}
                    <ProfileMobileSectionActions
                        isEditing={isEditing}
                        onEdit={handleEditClick}
                        onCancel={handleCancelClick}
                        onSave={handleSaveClick}
                        show={isEditPage && !hideEdit}
                    />
                </Card.Body>
            </Card>
        </>
    );
};

export default UserAddResidence;
